import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  filterArticles,
  allTags,
  allCategories,
  allAuthors,
  articles,
} from "@/mocks/articles";
import type { Article } from "@/mocks/articles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";

const PER_PAGE = 12;

/* ─── Active Filter Chip ─── */
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-hj-dark bg-hj-gray-light rounded-full border border-hj-gray-border">
      {label}
      <button
        onClick={onRemove}
        className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-hj-gray-border transition-colors cursor-pointer"
        aria-label={`Filter ${label} entfernen`}
      >
        <i className="ri-close-line text-xs"></i>
      </button>
    </span>
  );
}

/* ─── Search Result Card ─── */
function SearchResultCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/artikel/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl hover:bg-hj-gray-light/50 transition-colors border border-transparent hover:border-hj-gray-border"
    >
      <div className="w-full sm:w-48 h-32 shrink-0 overflow-hidden rounded-lg" style={{ minHeight: "128px" }}>
        <img
          src={article.imageUrl}
          alt={article.imageAlt || article.title}
          loading="lazy"
          width={192}
          height={128}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-hj-red uppercase tracking-wide font-body">
            {article.category}
          </span>
          {article.isBreaking && (
            <span className="px-1.5 py-0.5 text-xs font-bold text-white bg-hj-red rounded">
              Breaking
            </span>
          )}
        </div>
        <h3 className="text-base md:text-lg font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 mt-1 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-hj-gray mt-1.5 line-clamp-2 hidden sm:block font-body">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-hj-muted font-body flex-wrap">
          <span className="font-medium text-hj-dark">{article.author}</span>
          <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
          <time dateTime={article.publishedAt}>
            {new Date(article.publishedAt).toLocaleDateString("de-CH", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </time>
          <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
          <span>{article.readTime}</span>
          <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
          <span>{article.views.toLocaleString("de-CH")} Aufrufe</span>
        </div>
        {article.tags.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {article.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs text-hj-gray bg-hj-gray-light rounded-full font-body"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

/* ─── Pagination ─── */
function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="w-9 h-9 flex items-center justify-center rounded-md border border-hj-gray-border text-hj-dark hover:bg-hj-gray-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        aria-label="Vorherige Seite"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      {getPages().map((p, i) =>
        p === "..." ? (
          <span key={`dots-${i}`} className="px-2 text-sm text-hj-gray font-body">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p as number)}
            className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
              p === page
                ? "bg-hj-red text-white"
                : "border border-hj-gray-border text-hj-dark hover:bg-hj-gray-light"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="w-9 h-9 flex items-center justify-center rounded-md border border-hj-gray-border text-hj-dark hover:bg-hj-gray-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        aria-label="Nächste Seite"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>
    </div>
  );
}

/* ─── Search Page ─── */
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const query = searchParams.get("q") || "";
  const category = searchParams.get("cat") || "";
  const author = searchParams.get("autor") || "";
  const dateFrom = searchParams.get("von") || "";
  const dateTo = searchParams.get("bis") || "";
  const tagParam = searchParams.get("tag") || "";
  const sortBy = (searchParams.get("sort") as "relevance" | "newest" | "oldest" | "most_viewed" | "least_viewed") || "relevance";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const [searchInput, setSearchInput] = useState(query);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  const filters = useMemo(
    () => ({
      query,
      category: category || undefined,
      author: author || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      tags: tagParam ? [tagParam] : undefined,
      sortBy,
    }),
    [query, category, author, dateFrom, dateTo, tagParam, sortBy]
  );

  const result = useMemo(() => filterArticles(filters, page, PER_PAGE), [filters, page]);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.delete("page");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchInput.trim()) {
        updateParam("q", searchInput.trim());
      } else {
        updateParam("q", null);
      }
    },
    [searchInput, updateParam]
  );

  const handlePageChange = useCallback(
    (p: number) => {
      const next = new URLSearchParams(searchParams);
      if (p > 1) next.set("page", String(p));
      else next.delete("page");
      setSearchParams(next, { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [searchParams, setSearchParams]
  );

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; key: string }[] = [];
    if (query) chips.push({ label: `Suche: "${query}"`, key: "q" });
    if (category) {
      const catName = allCategories.find((c) => c.slug === category)?.name || category;
      chips.push({ label: catName, key: "cat" });
    }
    if (author) {
      const authorName = allAuthors.find((a) => a.slug === author)?.name || author;
      chips.push({ label: authorName, key: "autor" });
    }
    if (dateFrom) chips.push({ label: `Ab: ${dateFrom}`, key: "von" });
    if (dateTo) chips.push({ label: `Bis: ${dateTo}`, key: "bis" });
    if (tagParam) chips.push({ label: `#${tagParam}`, key: "tag" });
    return chips;
  }, [query, category, author, dateFrom, dateTo, tagParam]);

  const hasActiveFilters = activeFilterChips.length > 0;

  // Suggestions for no results
  const suggestions = useMemo(() => {
    if (result.total > 0) return [];
    // Find related articles by partial tag/category match
    const related = articles
      .filter((a) => a.status === "published")
      .filter((a) => {
        if (category && a.categorySlug === category) return true;
        if (tagParam && a.tags.some((t) => t.toLowerCase().includes(tagParam.toLowerCase()))) return true;
        if (query) {
          const q = query.toLowerCase();
          return a.tags.some((t) => t.toLowerCase().includes(q)) || a.category.toLowerCase().includes(q);
        }
        return false;
      })
      .slice(0, 4);
    return related;
  }, [result.total, category, tagParam, query]);

  // Popular tags (top 15)
  const popularTags = useMemo(() => {
    const tagCounts: Record<string, number> = {};
    articles.filter((a) => a.status === "published").forEach((a) => {
      a.tags.forEach((t) => {
        tagCounts[t] = (tagCounts[t] || 0) + 1;
      });
    });
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([tag]) => tag);
  }, []);

  const displayedTags = showAllTags ? allTags : popularTags;

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
      {/* Search Header */}
      <div className="bg-hj-gray-light border-b border-hj-gray-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <form onSubmit={handleSearch} className="max-w-2xl">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-hj-gray text-lg"></i>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Artikel, Themen, Autoren oder Tags suchen..."
                className="w-full pl-12 pr-14 py-3.5 md:py-4 bg-white border border-hj-gray-border rounded-xl text-base md:text-lg text-hj-dark placeholder-hj-muted focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red shadow-sm font-body"
                autoComplete="off"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(""); updateParam("q", null); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
                  aria-label="Suche löschen"
                >
                  <i className="ri-close-line text-hj-gray"></i>
                </button>
              )}
            </div>
          </form>

          {/* Active filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {activeFilterChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => updateParam(chip.key, null)}
                />
              ))}
              <button
                onClick={() => {
                  const next = new URLSearchParams();
                  if (query) next.set("q", query);
                  setSearchParams(next, { replace: true });
                }}
                className="text-sm text-hj-red hover:text-hj-red-light transition-colors font-body cursor-pointer"
              >
                Alle Filter löschen
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-64 shrink-0">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 bg-white border border-hj-gray-border rounded-lg text-sm font-medium text-hj-dark mb-4 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line"></i>
                Filter
                {hasActiveFilters && (
                  <span className="px-1.5 py-0.5 text-xs bg-hj-red text-white rounded-full">
                    {activeFilterChips.length}
                  </span>
                )}
              </span>
              <i className={mobileFiltersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>

            <div className={`${mobileFiltersOpen ? "block" : "hidden"} lg:block space-y-5`}>
              {/* Sort */}
              <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2 font-body">
                  <i className="ri-sort-desc text-hj-red"></i>
                  Sortierung
                </h3>
                <div className="space-y-1">
                  {[
                    { value: "relevance", label: "Relevanz" },
                    { value: "newest", label: "Neueste zuerst" },
                    { value: "oldest", label: "Älteste zuerst" },
                    { value: "most_viewed", label: "Meistgelesen" },
                    { value: "least_viewed", label: "Wenig gelesen" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateParam("sort", opt.value)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer font-body ${
                        sortBy === opt.value
                          ? "bg-hj-red/10 text-hj-red font-medium"
                          : "text-hj-gray hover:bg-hj-gray-light"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category filter */}
              <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2 font-body">
                  <i className="ri-folder-line text-hj-red"></i>
                  Kategorie
                </h3>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => updateParam("cat", null)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer font-body ${
                      !category ? "bg-hj-red/10 text-hj-red font-medium" : "text-hj-gray hover:bg-hj-gray-light"
                    }`}
                  >
                    Alle Kategorien
                  </button>
                  {allCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => updateParam("cat", cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer font-body ${
                        category === cat.slug
                          ? "bg-hj-red/10 text-hj-red font-medium"
                          : "text-hj-gray hover:bg-hj-gray-light"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Author filter */}
              <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2 font-body">
                  <i className="ri-user-line text-hj-red"></i>
                  Autor
                </h3>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => updateParam("autor", null)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer font-body ${
                      !author ? "bg-hj-red/10 text-hj-red font-medium" : "text-hj-gray hover:bg-hj-gray-light"
                    }`}
                  >
                    Alle Autoren
                  </button>
                  {allAuthors.map((aut) => (
                    <button
                      key={aut.slug}
                      onClick={() => updateParam("autor", aut.slug)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer font-body ${
                        author === aut.slug
                          ? "bg-hj-red/10 text-hj-red font-medium"
                          : "text-hj-gray hover:bg-hj-gray-light"
                      }`}
                    >
                      {aut.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date filter */}
              <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2 font-body">
                  <i className="ri-calendar-line text-hj-red"></i>
                  Zeitraum
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-hj-gray mb-1 block font-body">Von</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => updateParam("von", e.target.value || null)}
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-hj-gray mb-1 block font-body">Bis</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => updateParam("bis", e.target.value || null)}
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-body"
                    />
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-lg border border-hj-gray-border p-4">
                <h3 className="text-sm font-semibold text-hj-dark mb-3 flex items-center gap-2 font-body">
                  <i className="ri-price-tag-3-line text-hj-red"></i>
                  Tags
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {displayedTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => updateParam("tag", tagParam === tag ? null : tag)}
                      className={`px-2.5 py-1 text-xs rounded-full transition-colors cursor-pointer font-body ${
                        tagParam === tag
                          ? "bg-hj-red text-white"
                          : "bg-hj-gray-light text-hj-gray hover:bg-hj-gray-border"
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
                {allTags.length > popularTags.length && (
                  <button
                    onClick={() => setShowAllTags(!showAllTags)}
                    className="mt-2 text-xs text-hj-red hover:text-hj-red-light transition-colors font-body cursor-pointer"
                  >
                    {showAllTags ? "Weniger anzeigen" : `Alle ${allTags.length} Tags anzeigen`}
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1 min-w-0">
            {/* Results header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <p className="text-sm text-hj-gray font-body">
                {result.total > 0 ? (
                  <>
                    <strong className="text-hj-dark">{result.total}</strong> Ergebnis{result.total !== 1 ? "se" : ""}
                    {result.totalPages > 1 && (
                      <span> — Seite <strong className="text-hj-dark">{result.page}</strong> von {result.totalPages}</span>
                    )}
                  </>
                ) : (
                  "Keine Ergebnisse"
                )}
              </p>
              {/* Desktop sort dropdown */}
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-hj-gray font-body">Sortiert nach:</span>
                <select
                  value={sortBy}
                  onChange={(e) => updateParam("sort", e.target.value)}
                  className="px-3 py-1.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white font-body"
                >
                  <option value="relevance">Relevanz</option>
                  <option value="newest">Neueste zuerst</option>
                  <option value="oldest">Älteste zuerst</option>
                  <option value="most_viewed">Meistgelesen</option>
                  <option value="least_viewed">Wenig gelesen</option>
                </select>
              </div>
            </div>

            {/* Results list */}
            {result.articles.length > 0 ? (
              <>
                {/* Mobile ad - search top */}
                <div className="md:hidden py-2">
                  <AdSlotDisplay position="mobile_search_top" />
                </div>

                <div className="space-y-1 divide-y divide-hj-gray-border/50">
                  {result.articles.slice(0, 4).map((article) => (
                    <SearchResultCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Mobile ad - between results */}
                <div className="md:hidden py-2">
                  <AdSlotDisplay position="mobile_search_between" />
                </div>

                <div className="space-y-1 divide-y divide-hj-gray-border/50">
                  {result.articles.slice(4).map((article) => (
                    <SearchResultCard key={article.id} article={article} />
                  ))}
                </div>

                {/* Mobile ad - before pagination/footer */}
                <div className="md:hidden py-2 mt-2">
                  <AdSlotDisplay position="mobile_search_footer" />
                </div>

                <Pagination
                  page={result.page}
                  totalPages={result.totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              /* No results */
              <div className="text-center py-12 md:py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-hj-gray-light flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-hj-muted"></i>
                </div>
                <h2 className="text-lg font-bold text-hj-dark mb-2 font-heading">
                  Keine Ergebnisse gefunden
                </h2>
                <p className="text-hj-gray max-w-md mx-auto font-body">
                  Versuchen Sie es mit einem anderen Suchbegriff, entfernen Sie Filter oder durchsuchen Sie unsere Kategorien.
                </p>

                {/* Related suggestions */}
                {suggestions.length > 0 && (
                  <div className="mt-8">
                    <p className="text-sm font-semibold text-hj-dark mb-3 font-body">
                      Das könnte Sie auch interessieren:
                    </p>
                    <div className="space-y-3 max-w-lg mx-auto">
                      {suggestions.map((article) => (
                        <Link
                          key={article.id}
                          to={`/artikel/${article.slug}`}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-hj-gray-light transition-colors text-left"
                        >
                          <div className="w-16 h-12 shrink-0 overflow-hidden rounded-md">
                            <img
                              src={article.imageUrl}
                              alt={article.title}
                              loading="lazy"
                              width={64}
                              height={48}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-hj-dark line-clamp-2 font-heading">
                              {article.title}
                            </p>
                            <p className="text-xs text-hj-gray mt-0.5 font-body">
                              {article.category} · {article.author}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category quick links */}
                <div className="mt-8">
                  <p className="text-sm font-semibold text-hj-dark mb-3 font-body">
                    Beliebte Kategorien:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {allCategories.slice(0, 8).map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => {
                          updateParam("cat", cat.slug);
                          updateParam("q", null);
                        }}
                        className="px-4 py-2 text-sm text-hj-red border border-hj-red rounded-lg hover:bg-hj-red hover:text-white transition-colors font-body cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}